import {
    AddressContract,
    AppSku,
    EntityType,
    RideEventContract,
    RideType,
    SnapshotImageContract,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    GeoBounds,
    GeoCoordinate,
    Image,
    MailingAddress,
} from '@polaris-customerweb/sharedcomponents.shared';
import {
    Geofence,
    PlannedRide,
    TrackedRide,
    UserContentItem,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { cloneDeep } from 'lodash-es';
import { Collection } from './collection';
import { ContentCollection } from './content-collection';

export class RideEvent implements UserContentItem {
    readonly type: EntityType = 'Event';
    thumbnail = '';

    private _address: MailingAddress;
    private _contentCollection: ContentCollection;
    private _creationDate: Date;
    private _endDate: Date;
    private _geoCoordinate: GeoCoordinate;
    private _images: Image[] = [];
    private _modificationDate?: Date;
    private _startDate: Date;
    private contract: RideEventContract;

    constructor(contract?: RideEventContract) {
        if (contract) {
            this.contract = cloneDeep(contract);
        } else {
            this.contract = {
                name: undefined,
                startDate: new Date().toISOString(),
            };
        }
    }

    get address(): MailingAddress {
        if (!this._address && this.contract.address) {
            const addressContract = this.contract.address;
            this._address = new MailingAddress({ ...addressContract });
        }

        return this._address;
    }
    set address(address: MailingAddress) {
        this._address = address;
        this.contract.address = address?.toContract() as AddressContract;
    }

    get allGeofences(): readonly Geofence[] {
        return this._contentCollection?.allGeofences || [];
    }

    get allImages(): Image[] {
        return this.coverImage
            ? [this.coverImage, ...this.images]
            : this.images;
    }

    get allPlannedRides(): readonly PlannedRide[] {
        return this._contentCollection?.allPlannedRides || [];
    }

    get allTrackedRides(): readonly TrackedRide[] {
        return this._contentCollection?.allTrackedRides || [];
    }

    get allWaypoints(): readonly Waypoint[] {
        return this._contentCollection?.allWaypoints ?? [];
    }

    get appSku(): AppSku {
        return this.contract.appSku || 'Unknown';
    }

    get bounds(): GeoBounds {
        return this._contentCollection?.bounds || new GeoBounds();
    }

    get collections(): readonly Collection[] {
        return this._contentCollection?.collections || [];
    }
    set collections(collections: readonly Collection[]) {
        this._contentCollection.collections = [...collections];
        this.setLinkedItemsFromContent();
    }

    get coverImage(): Image | undefined {
        return this.contract.coverImage
            ? new Image(this.contract.coverImage)
            : undefined;
    }
    set coverImage(image: Image | undefined) {
        this.contract.coverImage = image?.toContract();
    }

    get createdByName(): string {
        return this.contract.createdByName || '';
    }

    get creationDate(): Date {
        if (!this._creationDate && this.contract.creationDate) {
            this._creationDate = new Date(this.contract.creationDate);
        }

        return this._creationDate || undefined;
    }

    get description(): string {
        return this.contract.description || '';
    }
    set description(value: string) {
        this.contract.description = value;
    }

    get distanceInMeters(): number | undefined {
        return this.contract.totalDistanceInMeters;
    }

    get durationInSeconds(): number | undefined {
        return this.contract.totalDurationInSeconds;
    }

    get endDate(): Date {
        if (!this._endDate && this.contract.endDate) {
            this._endDate = new Date(this.contract.endDate);
        }

        return this._endDate || undefined;
    }
    set endDate(date: Date) {
        if (date) {
            this._endDate = date;
            this.contract.endDate = date.toISOString();
        }
    }

    get geoCoordinate(): GeoCoordinate {
        if (
            !this._geoCoordinate &&
            this.contract.longitude &&
            this.contract.latitude
        ) {
            this._geoCoordinate = GeoCoordinate.fromLongitudeAndLatitude(
                this.contract.longitude,
                this.contract.latitude
            );
        }

        return this._geoCoordinate;
    }
    set geoCoordinate(geoCoordinate: GeoCoordinate) {
        this.contract.longitude = geoCoordinate.lng;
        this.contract.latitude = geoCoordinate.lat;
        this._geoCoordinate = geoCoordinate.clone();
    }

    get geofences(): readonly Geofence[] {
        return this._contentCollection?.geofences || [];
    }
    set geofences(geofences: readonly Geofence[]) {
        this._contentCollection.geofences = [...geofences];
        this.setLinkedItemsFromContent();
    }

    get hasItems(): boolean {
        return this.itemCount > 0;
    }

    get id(): string {
        return this.contract.id || '';
    }
    set id(id: string) {
        this.contract.id = id;
    }

    get images(): Image[] {
        if (!this._images.length) {
            this._images = (this.contract.images || []).map(
                (imageContract) => new Image(imageContract)
            );
        }

        return this._images;
    }

    set images(images: Image[]) {
        this.contract.images = images.map((image) => image.toContract());
        this._images = [...images];
    }

    get isHydrated(): boolean {
        return Boolean(this._contentCollection);
    }

    get isPublic(): boolean {
        return this.contract.visibility === 'Public';
    }
    set isPublic(isPublic: boolean) {
        this.contract.visibility = isPublic ? 'Public' : 'Private';
    }

    get itemCount(): number {
        return this.contract.linkedItems?.length || 0;
    }

    get modificationDate(): Date | undefined {
        if (!this._modificationDate && this.contract.modificationDate) {
            this._modificationDate = new Date(this.contract.modificationDate);
        }

        return this._modificationDate;
    }
    set modificationDate(modificationDate: Date) {
        const modificationDateString = modificationDate
            ? modificationDate.toISOString()
            : undefined;
        this.contract.modificationDate = modificationDateString;
    }

    get name(): string {
        return this.contract.name || '';
    }
    set name(value: string) {
        this.contract.name = value;
    }

    get plannedRides(): readonly PlannedRide[] {
        return this._contentCollection?.plannedRides || [];
    }
    set plannedRides(plannedRides: readonly PlannedRide[]) {
        this._contentCollection.plannedRides = [...plannedRides];
        this.setLinkedItemsFromContent();
    }

    get riderId(): string {
        return this.contract.riderId || '';
    }

    get rideTypes(): RideType[] {
        return this.contract.rideTypes || [];
    }

    get snapshots(): readonly SnapshotImageContract[] {
        return this.contract.snapshots || [];
    }

    get startDate(): Date {
        if (!this._startDate && this.contract.startDate) {
            this._startDate = new Date(this.contract.startDate);
        }

        return this._startDate || undefined;
    }
    set startDate(date: Date) {
        this._startDate = date;
        this.contract.startDate = date.toISOString();
    }

    get trackedRides(): readonly TrackedRide[] {
        return this._contentCollection?.trackedRides || [];
    }
    set trackedRides(trackedRides: readonly TrackedRide[]) {
        this._contentCollection.trackedRides = [...trackedRides];
        this.setLinkedItemsFromContent();
    }

    get url(): string {
        return this.contract.url || '';
    }
    set url(url: string) {
        this.contract.url = url;
    }

    get waypoints(): readonly Waypoint[] {
        return this._contentCollection?.waypoints || [];
    }
    set waypoints(waypoints: readonly Waypoint[]) {
        this._contentCollection.waypoints = [...waypoints];
        this.setLinkedItemsFromContent();
    }

    addUserContent(
        geofences?: Geofence[],
        plannedRides?: PlannedRide[],
        trackedRides?: TrackedRide[],
        waypoints?: Waypoint[],
        collections?: Collection[]
    ): void {
        if (!this._contentCollection) {
            this.setContentCollection(new ContentCollection());
        }

        this._contentCollection.addUserContentArrays(
            geofences,
            plannedRides,
            trackedRides,
            waypoints,
            collections
        );
        this.setLinkedItemsFromContent();
    }

    clone(): RideEvent {
        const rideEvent = new RideEvent(this.toContract());
        rideEvent.setContentCollection(this._contentCollection);
        return rideEvent;
    }

    getCollection(collectionId: string): Collection | undefined {
        return this._contentCollection.getCollection(collectionId);
    }

    getGeofence(geofenceId: string): Geofence | undefined {
        return this._contentCollection.getGeofence(geofenceId);
    }

    getPlannedRide(plannedRideId: string): PlannedRide | undefined {
        return this._contentCollection.getPlannedRide(plannedRideId);
    }

    getTrackedRide(trackedRideId: string): TrackedRide | undefined {
        return this._contentCollection.getTrackedRide(trackedRideId);
    }

    getUserContentItem(
        id: string,
        type: EntityType
    ): UserContentItem | undefined {
        return this._contentCollection.getUserContentItem(id, type);
    }

    getWaypoint(waypointId: string): Waypoint | undefined {
        return this._contentCollection.getWaypoint(waypointId);
    }

    setContentCollection(
        contentCollection: ContentCollection,
        bounds?: GeoBounds
    ): void {
        this._contentCollection = contentCollection;
        if (bounds) {
            this._contentCollection.bounds = bounds;
        }
    }

    toContract(): RideEventContract {
        return cloneDeep(this.contract);
    }

    toGeoJson(): RideEventContract {
        return this.toContract();
    }

    toJSON(): RideEventContract {
        return this.toGeoJson();
    }

    updateModifiedDate() {
        this._modificationDate = undefined;
        this.contract.modificationDate = new Date().toISOString();
    }

    private setLinkedItemsFromContent(): void {
        this.contract.linkedItems = (
            this._contentCollection?.toLinkedItems() || []
        ).map((linkedItem) => linkedItem.toDto());
    }
}
